import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { FigureLabel, FigureValue } from '@/components/ui'
import { useReconciliationEntitled } from '@/modules/account'
import {
  type Transaction,
  useCheckTransactions,
  useProject,
  useStatementCoverage,
  useStatements,
} from '@/modules/projects'
import { formatDateRange, getProjectPath } from '@/utils'
import { resolveCaseSpan } from './caseActivity'
import { summarizeCoverage, summarizeReconciliation } from './coverage'
import { ProcessingCard } from './ProcessingCard'

interface CaseMastheadProps {
  slug: string
  transactions: Transaction[]
}

interface Figure {
  label: string
  value: string
}

interface HealthChipProps {
  to: string
  isComplete: boolean
  children: ReactNode
}

// Green when nothing is outstanding, amber when something still needs a look.
const HealthChip = ({ to, isComplete, children }: HealthChipProps) => (
  <Link
    to={to}
    className={`inline-flex cursor-pointer items-center gap-1.5 font-medium hover:underline ${
      isComplete ? 'text-emerald-600' : 'text-amber-600'
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        isComplete ? 'bg-emerald-500' : 'bg-amber-500'
      }`}
    />
    {children}
  </Link>
)

// The case at a glance: a period line with a coverage chip, and a row of plain figures
// (accounts, documents, pages, checks).
export const CaseMasthead = ({ slug, transactions }: CaseMastheadProps) => {
  const statements = useStatements()
  const project = useProject()
  const checks = useCheckTransactions()
  const coverage = useStatementCoverage()

  const span = resolveCaseSpan(coverage, transactions, statements)
  const coverageSummary = summarizeCoverage(coverage)
  const insightsPath = `${getProjectPath({ slug })}/insights`
  const isReconciliationEntitled = useReconciliationEntitled()
  const reconciliationSummary = summarizeReconciliation(coverage)
  const showsReconciliation =
    isReconciliationEntitled && reconciliationSummary.statementMonths > 0

  // Count accounts from coverage, the same source the Account mix card uses, so the two never
  // disagree. Coverage spans every account the case has statements for (cash and brokerage),
  // deduped by last-4.
  const accountCount = coverage?.accounts.length ?? 0

  const period =
    span.start && span.end
      ? formatDateRange(span.start, span.end, 'MMM yyyy')
      : 'No statements yet'

  const figures: Figure[] = [
    { label: 'Accounts', value: String(accountCount) },
    { label: 'Statements', value: String(statements.length) },
    { label: 'Pages', value: String(project?.numberOfPages ?? 0) },
  ]
  if (checks.length > 0) {
    figures.push({ label: 'Checks', value: String(checks.length) })
  }

  return (
    <ProcessingCard className="p-6">
      <div className="@container">
        <div className="flex flex-col gap-6 @3xl:flex-row @3xl:items-center @3xl:justify-between">
          <div>
            <FigureLabel>Case period</FigureLabel>
            <FigureValue className="mt-1">{period}</FigureValue>
            {coverageSummary.totalMonths > 0 ? (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <HealthChip
                  to={`${insightsPath}/coverage`}
                  isComplete={coverageSummary.isComplete}
                >
                  {coverageSummary.percent}% statement coverage
                </HealthChip>
                {showsReconciliation ? (
                  <HealthChip
                    to={`${insightsPath}/reconciliation`}
                    isComplete={reconciliationSummary.isComplete}
                  >
                    {reconciliationSummary.percent}% reconciled
                  </HealthChip>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center divide-x divide-border">
            {figures.map((figure) => (
              <div key={figure.label} className="px-7 first:pl-0 last:pr-0">
                <FigureLabel>{figure.label}</FigureLabel>
                <FigureValue className="mt-1">{figure.value}</FigureValue>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ProcessingCard>
  )
}
